import { Map as ImmutableMap, type RecordOf } from 'immutable';
import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { coreSettings } from '../../utils/core';
import { EPOCH_ISO } from '../../utils/date';
import { datasetsSelectors } from '../datasets';
import type { Dataset } from '../datasets/datasetsTypes';
import { dateSelectors } from '../date';
import { entitlementsSelectors } from '../entitlements';
import { preferencesSelectors } from '../preferences';
import { SUBSCRIPTIONS_REDUCER_KEY } from './subscriptionsReducer';
import type { SubscriptionRecord, SubscriptionsStore } from './subscriptionsTypes';

const PAYMENT_METHODS_REDUCER_KEY = 'paymentMethodsStore' as const;

type State = {
  [SUBSCRIPTIONS_REDUCER_KEY]: SubscriptionsStore;
  [PAYMENT_METHODS_REDUCER_KEY]?: {
    loadPending?: boolean;
    resourcesById?: ImmutableMap<string, PaymentMethodRecord>;
  };
};

type EntitlementLike = {
  id?: string;
};

type EntitlementsCollectionLike = {
  valueSeq?: () => {
    toArray: () => EntitlementLike[];
  };
};

type SizedCollectionLike = {
  size: number;
};

type PaymentMethodProps = {
  id?: string | null;
  clientId?: string | null;
  token?: string | null;
  deleted?: boolean | null;
  defaultMethod?: boolean | null;
  isDeleted?: boolean | null;
  isDeclined?: boolean | null;
  isHardDeclined?: boolean | null;
};

type PaymentMethodRecord = RecordOf<PaymentMethodProps>;

const EMPTY_PAYMENT_METHODS = ImmutableMap<string, PaymentMethodRecord>();

export type SubscriptionTrackingProps = {
  datasetEntitlements?: Array<string | undefined>;
  datasetTier?: string;
  subscriptionStatus?: string;
  subscriber?: string;
  subscriptionDays?: number;
  purchaseChannel?: string;
  subscriptionTier?: string;
  subscriptionTierName?: string;
  subscriptionExpireOn?: string;
  subscriptionState?: string;
  subscriptionTerm?: string;
  autoRenew?: string;
} & Record<string, string | number | Array<string | undefined> | undefined>;

export const getStore = (state: State): SubscriptionsStore => state[SUBSCRIPTIONS_REDUCER_KEY];

export const getIsLoading = (state: State): boolean => getStore(state).isLoading;
export const getLoadPending = (state: State): boolean => getStore(state).loadPending;
export const getError = (state: State): unknown => getStore(state).error;
export const getSubscriptions = (state: State) => getStore(state).resourcesById;
export const getResourcesById = getSubscriptions;

const getPaymentMethodsStore = (state: State) => state[PAYMENT_METHODS_REDUCER_KEY];
const getPaymentMethodsLoadPending = (state: State): boolean => Boolean(getPaymentMethodsStore(state)?.loadPending);
const getPaymentMethodsById = (state: State): ImmutableMap<string, PaymentMethodRecord> =>
  getPaymentMethodsStore(state)?.resourcesById ?? EMPTY_PAYMENT_METHODS;
const getPreferredPaymentMethods = createSelector(getPaymentMethodsById, (paymentMethods) =>
  paymentMethods.filter((paymentMethod) => paymentMethod.defaultMethod),
);
const getValidPreferredPaymentMethods = createSelector(getPreferredPaymentMethods, (paymentMethods) =>
  paymentMethods.filter(
    (paymentMethod) => paymentMethod.token && !paymentMethod.isDeclined && !paymentMethod.isHardDeclined,
  ),
);
const getInvalidPreferredPaymentMethods = createSelector(getPreferredPaymentMethods, (paymentMethods) =>
  paymentMethods.filter((paymentMethod) => paymentMethod.isDeclined || paymentMethod.isHardDeclined),
);

export const getActiveSubscription: (state: State) => SubscriptionRecord | null | undefined = createSelector(
  getSubscriptions,
  (subscriptionsById) => {
    const tiers = coreSettings.tiers ?? [];
    if (subscriptionsById.size > 0) {
      return subscriptionsById
        .filter((subscription) =>
          Boolean(subscription.active && subscription.tierUriName && tiers.includes(subscription.tierUriName)),
        )
        .sort((subscription1, subscription2) => {
          const tierComparison =
            tiers.indexOf(subscription1.tierUriName ?? '') - tiers.indexOf(subscription2.tierUriName ?? '');
          return tierComparison || (subscription1.expireOn ?? '').localeCompare(subscription2.expireOn ?? '');
        })
        .last();
    }
    return null;
  },
);

export const selectActiveOrLastSimplifiSubscription: (state: State) => SubscriptionRecord | null | undefined =
  createSelector(
    getActiveSubscription,
    getSubscriptions,
    (activeSubscription, subscriptions) =>
      activeSubscription || subscriptions?.find((subscription) => !!subscription?.tierUriName?.startsWith('acme')),
  );

export const getSubscriptionTrackingProps = createSelector(
  selectActiveOrLastSimplifiSubscription,
  entitlementsSelectors.getActiveEntitlements,
  entitlementsSelectors.getTopTierEntitlement,
  datasetsSelectors.getDatasetsById,
  (_state: State, datasetId?: string | null) => datasetId,
  dateSelectors.getCurrentDateString,
  (
    subscription: SubscriptionRecord | null | undefined,
    activeEntitlements: EntitlementsCollectionLike | undefined,
    datasetEntitlement: EntitlementLike | undefined,
    datasets: ImmutableMap<string, Dataset> | undefined,
    datasetId: string | null | undefined,
    currentDate: string,
  ): SubscriptionTrackingProps => {
    const dataset = datasetId ? datasets?.get(datasetId) : undefined;
    const datasetEntitlements = activeEntitlements
      ?.valueSeq?.()
      .toArray()
      .map(({ id }) => id);

    const props: SubscriptionTrackingProps = {};

    props.datasetEntitlements = datasetEntitlements;
    props.datasetTier = datasetEntitlement?.id;

    // Following code is copied from a helper that existed before (subscriptions/helpers.js).
    // https://quickenteam.atlassian.net/browse/SIMPL-19497
    if (subscription) {
      // Retaining this existing old prop for now, it is sent to mixpanel, LD and Pendo.
      // Not sure what the impact is if I remove it.
      props.subscriptionStatus = 'subscribed';

      props.subscriber = subscription.active ? 'Subscribed' : 'Expired';

      props.subscriptionStartOn = subscription.effectiveAt;

      props.subscriptionDays = subscription.effectiveAt
        ? Math.floor(DateTime.fromISO(currentDate).diff(DateTime.fromISO(subscription.effectiveAt)).as('days'))
        : undefined;

      let purchaseChannel;
      switch (subscription.merchant) {
        case 'Quicken':
          purchaseChannel = 'direct';
          break;
        case 'Apple':
          purchaseChannel = 'apple';
          break;
        case 'Google':
          purchaseChannel = 'google';
          break;
        default:
      }
      props.purchaseChannel = purchaseChannel;

      props.subscriptionTier = subscription.tierUriName;
      props.subscriptionTierName = subscription.tierName;
      props.subscriptionExpireOn = subscription.expireOn;

      if (subscription.isInTrialPeriod != null) {
        props.subscriptionState = subscription.isInTrialPeriod ? 'trial' : 'paid';
      }

      if (subscription.renewalFrequency != null) {
        props.subscriptionTerm = subscription.renewalFrequency === 'P1M' ? 'monthly' : 'annual';
      }

      props.autoRenew = subscription.autoRenew ? 'on' : 'off';
    } else if (dataset?.shared) {
      // For a shared user, we will not have access to the actual subscription record.
      // More details here: https://quickenteam.atlassian.net/browse/SIMPL-19497
      props.subscriber = 'Subscribed Shared';
    } else {
      props.subscriber = 'Not Subscribed';
      // Retaining this existing old prop for now, it is sent to mixpanel, LD and Pendo.
      // Not sure what the impact is if I remove it.
      props.subscriptionStatus = 'Not Subscribed';
    }

    return props;
  },
);

export const getProductSubscriptions = createSelector(getSubscriptions, (subscriptionsById) =>
  subscriptionsById.filter((subscription) => {
    const tiers = coreSettings.tiers ?? [];
    return Boolean(subscription.tierUriName && tiers.includes(subscription.tierUriName));
  }),
);
// ^ only gives subscriptions for tiers specified in core settings. Needed to verify product-specific subscriptions

export const subscriptionMessages = Object.freeze({
  NO_MESSAGE: 'NO_MESSAGE',
  MESSAGE1_1_ESTORE_TRIAL_CANCEL: 'MESSAGE1_1_ESTORE_TRIAL_CANCEL',
  MESSAGE1_2_MOBILE_TRIAL_CANCEL: 'MESSAGE1_2_MOBILE_TRIAL_CANCEL',
  MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL: 'MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL',
  MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL: 'MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL',
  MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL: 'MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL',
  MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL: 'MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL',
  MESSAGE4_ESTORE_NO_PAYMENT_TOKEN: 'MESSAGE4_ESTORE_NO_PAYMENT_TOKEN',
  MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD: 'MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD',
  MESSAGE6_ESTORE_GRACE_PERIOD: 'MESSAGE6_ESTORE_GRACE_PERIOD',
  MESSAGE7_MOBILE_GRACE_PERIOD: 'MESSAGE7_MOBILE_GRACE_PERIOD',
  MESSAGE9_CONVERT_MONTHLY_TO_YEARLY: 'MESSAGE9_CONVERT_MONTHLY_TO_YEARLY',
} as const);

export type SubscriptionMessage = (typeof subscriptionMessages)[keyof typeof subscriptionMessages];

const getAnnualConversionMessageDismissedOn = (
  state: Parameters<typeof preferencesSelectors.getPreferences>[0],
): string | undefined =>
  preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user' })?.annualConversionMessageDismissedOn ??
  undefined;

export const getSubscriptionMessage = createSelector(
  dateSelectors.getCurrentDateString,
  getActiveSubscription,
  getPaymentMethodsLoadPending,
  getValidPreferredPaymentMethods,
  getInvalidPreferredPaymentMethods,
  getAnnualConversionMessageDismissedOn,
  (
    todayISO: string,
    subscription: SubscriptionRecord | null | undefined,
    paymentMethodsLoadPending: boolean,
    validPaymentMethods: SizedCollectionLike,
    invalidPaymentMethods: SizedCollectionLike,
    annualConversionMessageDismissedOnISO: string | undefined,
  ): SubscriptionMessage => {
    let subscriptionMessage: SubscriptionMessage = subscriptionMessages.NO_MESSAGE;
    const today = DateTime.fromISO(todayISO);
    if (subscription?.active) {
      const expireOn = DateTime.fromISO(subscription.expireOn ?? '');
      const daysToExpire = expireOn.diff(today).as('days') + 1;
      if (subscription.autoRenew) {
        // subscription active
        if (subscription.isInGracePeriod) {
          // 6/7
          if (subscription.merchant === 'Quicken') {
            subscriptionMessage = subscriptionMessages.MESSAGE6_ESTORE_GRACE_PERIOD;
          } else {
            subscriptionMessage = subscriptionMessages.MESSAGE7_MOBILE_GRACE_PERIOD;
          }
        } else if (
          !paymentMethodsLoadPending &&
          !validPaymentMethods.size &&
          subscription.merchant === 'Quicken' &&
          daysToExpire <= 14
        ) {
          if (invalidPaymentMethods.size) {
            // 5
            subscriptionMessage = subscriptionMessages.MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD;
          } else {
            // 4
            subscriptionMessage = subscriptionMessages.MESSAGE4_ESTORE_NO_PAYMENT_TOKEN;
          }
        } else if (
          subscription.renewalFrequency === 'P1M' &&
          subscription.merchant === 'Quicken' &&
          subscription.effectiveAt &&
          daysToExpire <= 30
        ) {
          const effectiveAt = DateTime.fromISO(subscription.effectiveAt, {
            zone: 'utc',
          });
          const monthsUsed = today.diff(effectiveAt).as('months');
          if (monthsUsed >= 3) {
            const annualConversionMessageDismissedOn = DateTime.fromISO(
              annualConversionMessageDismissedOnISO || EPOCH_ISO,
              { zone: 'utc' },
            );
            const monthsPassed = today.diff(annualConversionMessageDismissedOn).as('months');
            if (monthsPassed >= 3) {
              subscriptionMessage = subscriptionMessages.MESSAGE9_CONVERT_MONTHLY_TO_YEARLY;
            }
          }
        }
      } else {
        // subscription cancelled
        if (subscription.isInTrialPeriod) {
          if (daysToExpire > 0 && daysToExpire <= 7) {
            if (subscription.merchant === 'Quicken') {
              // 1.1 - trial estore user cancelled subscription
              subscriptionMessage = subscriptionMessages.MESSAGE1_1_ESTORE_TRIAL_CANCEL;
            } else {
              // 1.2 - trial appstore user cancelled subscription
              subscriptionMessage = subscriptionMessages.MESSAGE1_2_MOBILE_TRIAL_CANCEL;
            }
          }
        } else if (subscription.renewalFrequency === 'P1M' && daysToExpire > 0 && daysToExpire <= 7) {
          if (subscription.merchant === 'Quicken') {
            // 2.1 - paid estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL;
          } else {
            // 2.2 - paid non estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL;
          }
        } else if (subscription.renewalFrequency === 'P1Y' && daysToExpire > 0 && daysToExpire <= 30) {
          if (subscription.merchant === 'Quicken') {
            // 3.1 - paid estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL;
          } else {
            // 3.2 - paid non estore user cancelled subscription
            subscriptionMessage = subscriptionMessages.MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL;
          }
        }
      }
    }

    return subscriptionMessage;
  },
);
